import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  Person as PersonIcon,
  TrendingUp as PerformanceIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { EducationModal, type EducationStep } from '../shared/EducationModal';

// Visual: Manager-based view
const ManagerViewVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
      Your balances by manager
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 200, mx: 'auto' }}>
      {[
        { name: 'John D.', balance: '-$25', color: 'error' },
        { name: 'Sarah M.', balance: '+$150', color: 'success' },
        { name: 'Mike R.', balance: '$0', color: 'grey' },
      ].map((manager) => (
        <Paper key={manager.name} elevation={0} sx={{ p: 1, display: 'flex', justifyContent: 'space-between', bgcolor: 'grey.50', border: 1, borderColor: 'grey.200' }}>
          <Typography variant="caption">{manager.name}</Typography>
          <Typography variant="caption" fontWeight="bold" color={`${manager.color}.main`}>{manager.balance}</Typography>
        </Paper>
      ))}
    </Box>
  </Box>
);

// Visual: Balance types
const BalanceTypesVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
    <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'error.50', border: 1, borderColor: 'error.200', minWidth: 75 }}>
      <Typography variant="caption" fontWeight="bold" color="error.dark" display="block">I Owe</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>Pay entries</Typography>
    </Paper>
    <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.50', border: 1, borderColor: 'success.200', minWidth: 75 }}>
      <Typography variant="caption" fontWeight="bold" color="success.dark" display="block">Owed to Me</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>Collect wins</Typography>
    </Paper>
    <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.100', border: 1, borderColor: 'grey.300', minWidth: 75 }}>
      <CheckIcon sx={{ color: 'grey.600', fontSize: 16 }} />
      <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block">Settled</Typography>
    </Paper>
  </Box>
);

// Visual: Performance metrics
const PerformanceVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">Total Spent</Typography>
        <Typography variant="h6" color="error.main">$500</Typography>
      </Box>
      <Box sx={{ borderLeft: 1, borderColor: 'divider', pl: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block">Total Won</Typography>
        <Typography variant="h6" color="success.main">$750</Typography>
      </Box>
    </Box>
    <Chip label="Overall: +$250" color="success" size="small" />
  </Box>
);

const steps: EducationStep[] = [
  {
    title: 'Your Balances',
    description: 'See your financial standing across all board managers. Balances are grouped by manager, combining all boards you\'ve played together.',
    icon: <BalanceIcon sx={{ fontSize: 40 }} />,
    visual: <ManagerViewVisual />,
  },
  {
    title: 'Three Balance Types',
    description: '"I Owe" means you need to pay entry fees. "Owed to Me" means managers owe you winnings. "Settled" means all payments are complete.',
    icon: <PersonIcon sx={{ fontSize: 40 }} />,
    visual: <BalanceTypesVisual />,
  },
  {
    title: 'Track Your Performance',
    description: 'View your overall performance across all games - total spent on entries versus total winnings earned. See if you\'re ahead or behind!',
    icon: <PerformanceIcon sx={{ fontSize: 40 }} />,
    visual: <PerformanceVisual />,
  },
];

interface BalancesEducationModalProps {
  open: boolean;
  onClose: () => void;
}

export const BalancesEducationModal: React.FC<BalancesEducationModalProps> = ({ open, onClose }) => (
  <EducationModal
    open={open}
    onClose={onClose}
    modalId="balances"
    steps={steps}
    headerColor="info.main"
    finishButtonText="View Balances"
    buttonColor="info"
  />
);

export default BalancesEducationModal;
